package com.videoanalyzer.web.components.analysis

import androidx.compose.runtime.Composable
import com.videoanalyzer.web.models.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Detailed analysis card components for extended video analysis.
 * Contains 13 specialized analysis cards covering performance, engagement,
 * SEO, retention, niche, timing, and strategy recommendations.
 */

// ============================================================
// Extended Analysis Sections Orchestrator
// ============================================================

@Composable
fun ExtendedAnalysisSections(analysis: FullAnalysis) {
    // Section header
    Div({
        style {
            marginTop(40.px)
            marginBottom(24.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
            }
        }) {
            Div({
                style {
                    width(44.px)
                    height(44.px)
                    borderRadius(8.px)
                    backgroundColor(Color("#27272A"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(22.px)
                }
            }) { Text("📊") }

            H3({
                style {
                    fontSize(22.px)
                    fontWeight(700)
                    color(Color("#FAFAFA"))
                }
            }) { Text("Detailed Analysis") }
        }
    }

    // Performance & Engagement Row
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fit, minmax(300px, 1fr))")
            gap(20.px)
            marginBottom(20.px)
        }
    }) {
        analysis.performanceAnalysis?.let { PerformanceCard(it) }
        analysis.engagementAnalysis?.let { EngagementCard(it) }
        analysis.topicDemandAnalysis?.let { TopicDemandCard(it) }
    }

    // Content & SEO Row
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fit, minmax(300px, 1fr))")
            gap(20.px)
            marginBottom(20.px)
        }
    }) {
        analysis.descriptionAnalysis?.let { DescriptionCard(it) }
        analysis.seoTagsAnalysis?.let { SeoTagsCard(it) }
        analysis.chaptersAnalysis?.let { ChaptersCard(it) }
    }

    // Retention & Audience Row
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fit, minmax(300px, 1fr))")
            gap(20.px)
            marginBottom(20.px)
        }
    }) {
        analysis.retentionAnalysis?.let { RetentionCard(it) }
        analysis.nicheAnalysis?.let { NicheCard(it) }
        analysis.pacingAnalysis?.let { PacingCard(it) }
    }

    // Timing & Strategy Row
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fit, minmax(300px, 1fr))")
            gap(20.px)
            marginBottom(20.px)
        }
    }) {
        analysis.timingAnalysis?.let { TimingCard(it) }
        analysis.consistencyAnalysis?.let { ConsistencyCard(it) }
        analysis.channelInsights?.let { ChannelInsightsCard(it) }
    }

    // Strategy Recommendations (full width)
    analysis.strategyRecommendations?.let { StrategyCard(it) }
}

// ============================================================
// Performance & Engagement Cards
// ============================================================

@Composable
fun PerformanceCard(analysis: PerformanceAnalysis) {
    AnalysisSectionCard(
        icon = "📈",
        title = "Performance",
        score = analysis.potentialScore,
        gradientStart = "rgba(16, 185, 129, 0.2)",
        gradientEnd = "rgba(5, 150, 105, 0.2)"
    ) {
        // Stats grid
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(3, 1fr)")
                gap(12.px)
                marginBottom(16.px)
            }
        }) {
            StatItem("Views", formatNumber(analysis.currentViews))
            StatItem("Likes", formatNumber(analysis.currentLikes))
            StatItem("Comments", formatNumber(analysis.currentComments))
        }

        // Rating badges
        Div({
            style {
                display(DisplayStyle.Flex)
                flexWrap(FlexWrap.Wrap)
                gap(8.px)
                marginBottom(12.px)
            }
        }) {
            RatingBadge("Performance", analysis.performanceRating)
            RatingBadge("Engagement", analysis.engagementRating)
        }

        if (analysis.performanceInsight.isNotBlank()) {
            InsightText(analysis.performanceInsight)
        }
        if (analysis.potentialInsight.isNotBlank()) {
            InsightText(analysis.potentialInsight)
        }
    }
}

@Composable
fun EngagementCard(analysis: EngagementAnalysis) {
    AnalysisSectionCard(
        icon = "💬",
        title = "Engagement",
        score = analysis.score,
        gradientStart = "rgba(244, 114, 182, 0.2)",
        gradientEnd = "rgba(236, 72, 153, 0.2)"
    ) {
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(2, 1fr)")
                gap(12.px)
                marginBottom(16.px)
            }
        }) {
            StatItem("Like Ratio", "${analysis.likeToViewRatio.asDynamic().toFixed(2)}%")
            StatItem("Comment Ratio", "${analysis.commentToViewRatio.asDynamic().toFixed(4)}%")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexWrap(FlexWrap.Wrap)
                gap(8.px)
                marginBottom(12.px)
            }
        }) {
            RatingBadge("Quality", analysis.engagementQuality)
            RatingBadge("Viral Potential", analysis.viralPotential)
        }

        SuggestionsList(analysis.suggestions)
    }
}

@Composable
fun TopicDemandCard(analysis: TopicDemandAnalysis) {
    AnalysisSectionCard(
        icon = "🔥",
        title = "Topic & Demand",
        score = analysis.score,
        gradientStart = "rgba(251, 146, 60, 0.2)",
        gradientEnd = "rgba(234, 88, 12, 0.2)"
    ) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexWrap(FlexWrap.Wrap)
                gap(8.px)
                marginBottom(16.px)
            }
        }) {
            RatingBadge("Relevance", analysis.topicRelevance)
            RatingBadge("Search Demand", analysis.searchDemand)
            RatingBadge("Competition", analysis.competitionLevel)
        }

        // Trending score bar
        Div({
            style {
                marginBottom(12.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    marginBottom(4.px)
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        color(Color("#94a3b8"))
                    }
                }) { Text("Trending Score") }
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(600)
                        color(Color(getScoreColor(analysis.trendingScore)))
                    }
                }) { Text("${analysis.trendingScore}/100") }
            }
            Div({ classes("progress-bar") }) {
                Div({
                    classes("progress-fill")
                    style {
                        width(analysis.trendingScore.percent)
                        backgroundColor(Color("#F59E0B"))
                    }
                })
            }
        }

        SuggestionsList(analysis.suggestions)
    }
}

// ============================================================
// Content & SEO Cards
// ============================================================

@Composable
fun DescriptionCard(analysis: DescriptionAnalysis) {
    AnalysisSectionCard(
        icon = "📄",
        title = "Description",
        score = analysis.score,
        gradientStart = "rgba(99, 102, 241, 0.2)",
        gradientEnd = "rgba(139, 92, 246, 0.2)"
    ) {
        // Feature checks
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(2, 1fr)")
                gap(8.px)
                marginBottom(16.px)
            }
        }) {
            FeatureCheck("Call to Action", analysis.hasCallToAction)
            FeatureCheck("Timestamps", analysis.hasTimestamps)
            FeatureCheck("Links", analysis.hasLinks)
            FeatureCheck("Hashtags", analysis.hasHashtags)
        }

        if (analysis.keywordsFound.isNotEmpty()) {
            Div({
                style { marginBottom(12.px) }
            }) {
                Span({
                    style {
                        fontSize(11.px)
                        color(Color("#64748b"))
                        fontWeight(500)
                        property("text-transform", "uppercase")
                    }
                }) { Text("Keywords Found") }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexWrap(FlexWrap.Wrap)
                        gap(6.px)
                        marginTop(8.px)
                    }
                }) {
                    analysis.keywordsFound.take(5).forEach { keyword ->
                        Tag(keyword, "rgba(99, 102, 241, 0.3)", "#818cf8")
                    }
                }
            }
        }

        SuggestionsList(analysis.suggestions)
    }
}

@Composable
fun SeoTagsCard(analysis: SeoTagsAnalysis) {
    AnalysisSectionCard(
        icon = "🏷️",
        title = "SEO & Tags",
        score = analysis.score,
        gradientStart = "rgba(34, 197, 94, 0.2)",
        gradientEnd = "rgba(22, 163, 74, 0.2)"
    ) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(16.px)
                marginBottom(16.px)
            }
        }) {
            StatItem("Tags", "${analysis.tagsCount}")
            RatingBadge("Quality", analysis.tagsQuality)
        }

        if (analysis.suggestedTags.isNotEmpty()) {
            Div({
                style { marginBottom(12.px) }
            }) {
                Span({
                    style {
                        fontSize(11.px)
                        color(Color("#64748b"))
                        fontWeight(500)
                        property("text-transform", "uppercase")
                    }
                }) { Text("Suggested Tags") }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexWrap(FlexWrap.Wrap)
                        gap(6.px)
                        marginTop(8.px)
                    }
                }) {
                    analysis.suggestedTags.take(6).forEach { tag ->
                        Tag(tag, "rgba(34, 197, 94, 0.3)", "#6ee7b7")
                    }
                }
            }
        }

        SuggestionsList(analysis.suggestions)
    }
}

@Composable
fun ChaptersCard(analysis: ChaptersAnalysis) {
    AnalysisSectionCard(
        icon = "📑",
        title = "Chapters",
        score = analysis.score,
        gradientStart = "rgba(168, 85, 247, 0.2)",
        gradientEnd = "rgba(139, 92, 246, 0.2)"
    ) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(16.px)
                marginBottom(16.px)
            }
        }) {
            FeatureCheck("Has Chapters", analysis.hasChapters)
            if (analysis.hasChapters) {
                StatItem("Count", "${analysis.chaptersCount}")
            }
            RatingBadge("Quality", analysis.chaptersQuality)
        }

        SuggestionsList(analysis.suggestions)
    }
}

// ============================================================
// Retention & Audience Cards
// ============================================================

@Composable
fun RetentionCard(analysis: RetentionAnalysis) {
    AnalysisSectionCard(
        icon = "⏱️",
        title = "Retention & Watch Time",
        score = analysis.score,
        gradientStart = "rgba(59, 130, 246, 0.2)",
        gradientEnd = "rgba(37, 99, 235, 0.2)"
    ) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexWrap(FlexWrap.Wrap)
                gap(8.px)
                marginBottom(16.px)
            }
        }) {
            RatingBadge("Hook Strength", analysis.hookStrength)
            RatingBadge("Est. Retention", analysis.estimatedRetention)
        }

        if (analysis.hookSuggestions.isNotEmpty()) {
            Div({
                style { marginBottom(12.px) }
            }) {
                Span({
                    style {
                        fontSize(11.px)
                        color(Color("#64748b"))
                        fontWeight(500)
                        property("text-transform", "uppercase")
                    }
                }) { Text("Hook Tips") }
                Div({
                    style {
                        marginTop(8.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(6.px)
                    }
                }) {
                    analysis.hookSuggestions.take(2).forEach { tip ->
                        MiniSuggestionItem(tip)
                    }
                }
            }
        }

        SuggestionsList(analysis.suggestions)
    }
}

@Composable
fun NicheCard(analysis: NicheAnalysis) {
    AnalysisSectionCard(
        icon = "🎯",
        title = "Niche & Audience",
        score = analysis.score,
        gradientStart = "rgba(236, 72, 153, 0.2)",
        gradientEnd = "rgba(219, 39, 119, 0.2)"
    ) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexWrap(FlexWrap.Wrap)
                gap(8.px)
                marginBottom(16.px)
            }
        }) {
            RatingBadge("Clarity", analysis.nicheClarity)
            RatingBadge("Audience Match", analysis.audienceMatch)
            RatingBadge("Authority", analysis.nicheAuthority)
        }

        if (analysis.targetAudience.isNotBlank()) {
            Div({
                style {
                    backgroundColor(Color("#27272A"))
                    padding(10.px, 12.px)
                    borderRadius(6.px)
                    marginBottom(12.px)
                }
            }) {
                Span({
                    style {
                        fontSize(11.px)
                        color(Color("#71717A"))
                        display(DisplayStyle.Block)
                        marginBottom(4.px)
                    }
                }) { Text("Target Audience") }
                Span({
                    style {
                        fontSize(13.px)
                        color(Color("#FAFAFA"))
                    }
                }) { Text(analysis.targetAudience) }
            }
        }

        SuggestionsList(analysis.suggestions)
    }
}

@Composable
fun PacingCard(analysis: PacingAnalysis) {
    AnalysisSectionCard(
        icon = "🎬",
        title = "Video Pacing",
        score = analysis.score,
        gradientStart = "rgba(14, 165, 233, 0.2)",
        gradientEnd = "rgba(2, 132, 199, 0.2)"
    ) {
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(2, 1fr)")
                gap(12.px)
                marginBottom(16.px)
            }
        }) {
            if (analysis.videoDuration.isNotBlank()) {
                StatItem("Duration", analysis.videoDuration)
            }
            if (analysis.idealDuration.isNotBlank()) {
                StatItem("Ideal", analysis.idealDuration)
            }
        }

        RatingBadge("Duration Fit", analysis.durationFit)

        if (analysis.editingTips.isNotEmpty()) {
            Div({
                style { marginTop(12.px) }
            }) {
                analysis.editingTips.take(2).forEach { tip ->
                    MiniSuggestionItem(tip)
                }
            }
        }

        SuggestionsList(analysis.suggestions)
    }
}

// ============================================================
// Timing & Consistency Cards
// ============================================================

@Composable
fun TimingCard(analysis: TimingAnalysis) {
    // Helper to check if a value is valid (not unknown/placeholder)
    fun isValidValue(value: String): Boolean {
        val lower = value.lowercase()
        return value.isNotBlank() &&
                !lower.contains("unknown") &&
                !lower.contains("n/a") &&
                !lower.contains("not available")
    }

    val hasValidDay = isValidValue(analysis.publishedDay)
    val hasValidTime = isValidValue(analysis.publishedTime)

    AnalysisSectionCard(
        icon = "🕐",
        title = "Publishing Timing",
        score = analysis.score,
        gradientStart = "rgba(245, 158, 11, 0.2)",
        gradientEnd = "rgba(217, 119, 6, 0.2)"
    ) {
        // Published info - stacked vertically
        if (hasValidDay || hasValidTime) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                    marginBottom(12.px)
                }
            }) {
                // Day and time tags in a row
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexWrap(FlexWrap.Wrap)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                }) {
                    if (hasValidDay) {
                        Tag(analysis.publishedDay, "rgba(245, 158, 11, 0.3)", "#fcd34d")
                    }
                    if (hasValidTime) {
                        Tag(analysis.publishedTime, "rgba(245, 158, 11, 0.3)", "#fcd34d")
                    }
                }
                // Optimal check on its own line
                FeatureCheck("Optimal Time", analysis.isOptimalTime)
            }
        }

        // Best days/times recommendations
        if (analysis.bestDays.isNotEmpty() || analysis.bestTimes.isNotEmpty()) {
            Div({
                style {
                    backgroundColor(Color("#27272A"))
                    padding(12.px)
                    borderRadius(6.px)
                    marginBottom(12.px)
                }
            }) {
                if (analysis.bestDays.isNotEmpty()) {
                    Div({
                        style { marginBottom(if (analysis.bestTimes.isNotEmpty()) 10.px else 0.px) }
                    }) {
                        Span({
                            style {
                                fontSize(11.px)
                                color(Color("#64748b"))
                                display(DisplayStyle.Block)
                                marginBottom(6.px)
                                fontWeight(500)
                                property("text-transform", "uppercase")
                            }
                        }) { Text("Best Days") }
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                flexWrap(FlexWrap.Wrap)
                                gap(6.px)
                            }
                        }) {
                            analysis.bestDays.forEach { day ->
                                Tag(day, "rgba(245, 158, 11, 0.2)", "#fcd34d")
                            }
                        }
                    }
                }
                if (analysis.bestTimes.isNotEmpty()) {
                    Div {
                        Span({
                            style {
                                fontSize(11.px)
                                color(Color("#64748b"))
                                display(DisplayStyle.Block)
                                marginBottom(6.px)
                                fontWeight(500)
                                property("text-transform", "uppercase")
                            }
                        }) { Text("Best Times") }
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                flexWrap(FlexWrap.Wrap)
                                gap(6.px)
                            }
                        }) {
                            analysis.bestTimes.forEach { time ->
                                Tag(time, "rgba(245, 158, 11, 0.2)", "#fcd34d")
                            }
                        }
                    }
                }
            }
        }

        SuggestionsList(analysis.suggestions)
    }
}

@Composable
fun ConsistencyCard(analysis: ConsistencyAnalysis) {
    AnalysisSectionCard(
        icon = "📅",
        title = "Upload Consistency",
        score = analysis.score,
        gradientStart = "rgba(139, 92, 246, 0.2)",
        gradientEnd = "rgba(124, 58, 237, 0.2)"
    ) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(16.px)
                marginBottom(16.px)
            }
        }) {
            StatItem("Videos", "${analysis.channelVideoCount}")
            RatingBadge("Consistency", analysis.consistencyRating)
        }

        SuggestionsList(analysis.suggestions)
    }
}

@Composable
fun ChannelInsightsCard(analysis: ChannelInsights) {
    AnalysisSectionCard(
        icon = "📺",
        title = "Channel Insights",
        score = null,
        gradientStart = "rgba(239, 68, 68, 0.2)",
        gradientEnd = "rgba(220, 38, 38, 0.2)"
    ) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(16.px)
                marginBottom(16.px)
            }
        }) {
            StatItem("Subscribers", formatNumber(analysis.subscriberCount))
            RatingBadge("Size", analysis.channelSize)
        }

        if (analysis.competitorInsight.isNotBlank()) {
            InsightText(analysis.competitorInsight)
        }

        if (analysis.nicheTips.isNotEmpty()) {
            Div({
                style { marginTop(12.px) }
            }) {
                analysis.nicheTips.take(3).forEach { tip ->
                    MiniSuggestionItem(tip)
                }
            }
        }
    }
}

// ============================================================
// Strategy Recommendations Card
// ============================================================

@Composable
fun StrategyCard(analysis: StrategyRecommendations) {
    Div({
        classes("card", "fade-in-up")
        style { marginTop(20.px) }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
                marginBottom(20.px)
            }
        }) {
            Div({
                style {
                    width(44.px)
                    height(44.px)
                    borderRadius(8.px)
                    backgroundColor(Color("#27272A"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(22.px)
                }
            }) { Text("🚀") }

            H3({
                style {
                    fontSize(18.px)
                    fontWeight(700)
                    color(Color("#FAFAFA"))
                }
            }) { Text("Strategy Recommendations") }
        }

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(280px, 1fr))")
                gap(16.px)
            }
        }) {
            if (analysis.shortsRecommendation.isNotBlank()) {
                StrategyItem("📱", "Shorts Strategy", analysis.shortsRecommendation)
            }
            if (analysis.uploadTimingTip.isNotBlank()) {
                StrategyItem("⏰", "Upload Timing", analysis.uploadTimingTip)
            }
            if (analysis.seriesTip.isNotBlank()) {
                StrategyItem("📚", "Series Potential", analysis.seriesTip)
            }
        }

        if (analysis.engagementTips.isNotEmpty() || analysis.growthTips.isNotEmpty() || analysis.seoTips.isNotEmpty()) {
            Div({
                style {
                    marginTop(20.px)
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                    gap(16.px)
                }
            }) {
                if (analysis.engagementTips.isNotEmpty()) {
                    TipsList("💬 Engagement", analysis.engagementTips)
                }
                if (analysis.growthTips.isNotEmpty()) {
                    TipsList("📈 Growth", analysis.growthTips)
                }
                if (analysis.seoTips.isNotEmpty()) {
                    TipsList("🔍 SEO", analysis.seoTips)
                }
            }
        }
    }
}
